"use client"

import { Fragment, useState, type ReactNode } from "react"

import { useTranslations } from "next-intl"

import type { FacetOption } from "@/components/marketplace/listing/types"
import { Button, ChevronIcon, Pill, Stack, Text } from "@/components/ui"

interface CategoryFacetProps {
  options: FacetOption[]
  selected: string[]
  onChange: (ids: string[]) => void
}

const ROW = "text-fg hover:text-fg-brand w-full justify-start gap-200 py-100 font-normal"

const COLLAPSED_COUNT = 7

const CHILD_COLLAPSED_COUNT = 8

const Label = ({ children, tone }: { children: ReactNode; tone?: "brand" | "muted" }) => (
  <Text as="span" variant="body" family="text" tone={tone ?? "default"} truncate className={`min-w-0 flex-1 text-left ${tone ? "" : "text-fg-heading"}`}>
    {children}
  </Text>
)

const hasResults = (option: FacetOption): boolean => option.count === undefined || option.count > 0

const contains = (option: FacetOption, value: string | undefined): boolean =>
  value !== undefined && (option.value === value || (option.children ?? []).some((child) => contains(child, value)))

interface CategoryRowsProps {
  rows: FacetOption[]
  parent: string
  current?: string
  onChange: (ids: string[]) => void
}

const CategoryRows = ({ rows, parent, current, onChange }: CategoryRowsProps) => (
  <>
    {rows.map((row) => {
      const checked = row.value === current
      const open = contains(row, current)
      return (
        <Fragment key={row.value}>
          <Button variant="quiet" aria-pressed={checked} onClick={() => onChange(checked ? [parent] : [row.value])} className={ROW}>
            <span aria-hidden className={`size-2 shrink-0 rounded-full ${checked ? "bg-brand" : "bg-transparent"}`} />
            <Label tone={open ? "brand" : undefined}>{row.label}</Label>
          </Button>
          {open && (row.children ?? []).length > 0 && (
            <Stack gap="0" className="pl-400">
              <CategoryRows rows={row.children ?? []} parent={row.value} current={current} onChange={onChange} />
            </Stack>
          )}
        </Fragment>
      )
    })}
  </>
)

const ShowMore = ({ hidden, open, onToggle }: { hidden: number; open: boolean; onToggle: () => void }) => {
  const t = useTranslations("search")
  return (
    <Button variant="quiet" onClick={onToggle} className="font-text text-copy justify-start gap-100 py-100 font-normal">
      <ChevronIcon size={12} className={`transition-transform ${open ? "-rotate-90" : "rotate-90"}`} />
      {open ? t("categoryFacet.showLess") : t("categoryFacet.showMore", { count: hidden })}
    </Button>
  )
}

interface DepartmentCardProps {
  department: FacetOption
  current?: string
  onChange: (ids: string[]) => void
}

const DepartmentCard = ({ department, current, onChange }: DepartmentCardProps) => {
  const [showAll, setShowAll] = useState(false)
  const children = department.children ?? []
  const hidden = children.length - CHILD_COLLAPSED_COUNT
  const forceExpand = children.slice(CHILD_COLLAPSED_COUNT).some((child) => contains(child, current))
  const visible = showAll || forceExpand ? children : children.slice(0, CHILD_COLLAPSED_COUNT)
  return (
    <div className="bg-brand-subtle rounded-10x pb-350">
      <Pill
        size="xs"
        selected={current === department.value}
        className="bg-brand text-fg-inverted hover:bg-brand-hover w-full justify-start px-250"
        onClick={() => onChange(current === department.value ? [] : [department.value])}
      >
        {department.label}
      </Pill>
      {children.length > 0 && (
        <Stack gap="0" className="pt-350 pr-250 pl-400">
          <CategoryRows rows={visible} parent={department.value} current={current} onChange={onChange} />
          {!forceExpand && hidden > 0 && <ShowMore hidden={hidden} open={showAll} onToggle={() => setShowAll((value) => !value)} />}
        </Stack>
      )}
    </div>
  )
}

interface DepartmentRowsProps {
  departments: FacetOption[]
  muted?: boolean
  onChange: (ids: string[]) => void
}

const DepartmentRows = ({ departments, muted = false, onChange }: DepartmentRowsProps) => (
  <>
    {departments.map((department) => (
      <Button key={department.value} variant="quiet" aria-pressed={false} onClick={() => onChange([department.value])} className={ROW}>
        <Label tone={muted ? "muted" : undefined}>{department.label}</Label>
      </Button>
    ))}
  </>
)

export const CategoryFacet = ({ options, selected, onChange }: CategoryFacetProps) => {
  const t = useTranslations("search")
  const [current] = selected
  const [showAll, setShowAll] = useState(false)

  const open = options.find((option) => contains(option, current))
  const rest = options.filter((option) => option !== open)
  const others = [...rest.filter(hasResults), ...rest.filter((option) => !hasResults(option))]
  const collapsed = !showAll && others.length > COLLAPSED_COUNT
  const visible = collapsed ? others.slice(0, COLLAPSED_COUNT) : others
  const matching = visible.filter(hasResults)
  const unmatched = visible.filter((option) => !hasResults(option))

  return (
    <Stack gap="250">
      {open && <DepartmentCard department={open} current={current} onChange={onChange} />}
      {others.length > 0 && (
        <Stack gap="0">
          <DepartmentRows departments={matching} onChange={onChange} />
          {unmatched.length > 0 && (
            <>
              <Text as="p" variant="caption" family="text" tone="muted" className={matching.length > 0 ? "pt-300 pb-100" : "pb-100"}>
                {t("categoryFacet.otherCategories")}
              </Text>
              <DepartmentRows departments={unmatched} muted onChange={onChange} />
            </>
          )}
          {others.length > COLLAPSED_COUNT && <ShowMore hidden={others.length - COLLAPSED_COUNT} open={showAll} onToggle={() => setShowAll((value) => !value)} />}
        </Stack>
      )}
    </Stack>
  )
}
